import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Printer } from 'lucide-react';
import { buildPeriodCalculation, buildWorkbenchGroups, isPeriodEditable } from '../payroll/core/periodWorkbench';
import { formatPayrollSheetValue } from '../payroll/core/payrollSheet';
import { loadPeriodWorkbench } from '../payroll/data/periodActions';
import { sheetModelFromGroups } from '../payroll/data/exportPayroll';
import { PAYROLL_PATHS, canViewPayrollPage } from '../payroll/data/permissions';
import { AccessDenied, Spinner } from '../payroll/components/ui';

const ACCENT_CLASS = { alert: 'text-red-700', highlight: 'bg-yellow-100', total: 'font-bold', net: 'font-bold bg-green-50' };

const PRINT_STYLE = `
@media print {
  @page { size: A4 landscape; margin: 8mm; }
  .no-print { display: none !important; }
  .print-page { page-break-after: always; }
  .print-page:last-child { page-break-after: auto; }
  body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
}
.payroll-print table { border-collapse: collapse; width: 100%; }
.payroll-print th, .payroll-print td { border: 1px solid #555; padding: 2px 4px; font-size: 10px; }
.payroll-print th { background: #dce6f1; }
`;

const BangCongLuongIn = () => {
    const { maKy } = useParams();
    const [wb, setWb] = useState(null);
    const [error, setError] = useState(null);
    const [mode, setMode] = useState('bang');
    const canView = canViewPayrollPage(PAYROLL_PATHS.periods);

    useEffect(() => {
        if (!canView) return;
        loadPeriodWorkbench(maKy).then(setWb).catch((loadError) => setError(loadError.message));
    }, [canView, maKy]);

    const model = useMemo(() => {
        if (!wb) return null;
        try {
            const editable = isPeriodEditable(wb.period);
            const result = buildPeriodCalculation({ period: wb.period, details: wb.details, ref: wb.ref, templates: wb.templates });
            const groups = buildWorkbenchGroups({
                period: wb.period, details: wb.details, calculation: result.calculation, compile: result.compile, templateById: result.templateById,
                variableByCode: wb.ref.variableByCode, editable: false, useSnapshot: !editable,
            });
            return sheetModelFromGroups(wb.period, groups);
        } catch (buildError) {
            return { error: buildError.message };
        }
    }, [wb]);

    if (!canView) return <AccessDenied />;
    if (error || model?.error) return <div className="p-6 text-red-700">{error || model.error}</div>;
    if (!wb || !model) return <Spinner label="Đang chuẩn bị bản in..." />;

    const draft = wb.period.TRANG_THAI !== 'DA_DUYET';
    return (
        <div className="payroll-print bg-white min-h-screen p-4">
            <style>{PRINT_STYLE}</style>
            <div className="no-print flex items-center gap-3 mb-4">
                <select className="border border-gray-300 rounded px-2 py-1 text-sm" value={mode} onChange={(e) => setMode(e.target.value)}>
                    <option value="bang">Bảng lương ký nhận</option>
                    <option value="phieu">Phiếu lương từng người</option>
                </select>
                <button className="px-3 py-1.5 bg-blue-600 text-white rounded flex items-center gap-1.5 text-sm" onClick={() => window.print()}><Printer className="w-4 h-4" />In</button>
                {draft && <span className="text-sm text-red-700">Kỳ chưa duyệt: bản in là bản nháp.</span>}
            </div>
            {mode === 'bang' ? model.sheets.map((sheet) => (
                <div key={sheet.templateCode} className="print-page mb-8">
                    <h1 className="text-center text-base font-bold">{model.title}{draft ? ' (BẢN NHÁP)' : ''}</h1>
                    <p className="text-center text-xs mb-2">{sheet.templateName} — {wb.period.MA_KY}</p>
                    <table>
                        <thead>
                            <tr>
                                <th>STT</th><th>HỌ VÀ TÊN</th><th>TỔ</th>
                                {sheet.columns.map((column) => <th key={column.code}>{column.header}</th>)}
                                <th style={{ minWidth: 70 }}>KÝ NHẬN</th>
                            </tr>
                        </thead>
                        <tbody>
                            {sheet.rows.map((row, index) => {
                                const span = sheet.teamSpans.find((item) => item.start === index);
                                return (
                                    <tr key={row.soHd}>
                                        <td className="text-center">{row.stt}</td>
                                        <td className="whitespace-nowrap">{row.hoTen}</td>
                                        {span && <td rowSpan={span.length} className="text-center align-middle">{row.to}</td>}
                                        {sheet.columns.map((column) => (
                                            <td key={column.code} className={`text-right whitespace-nowrap ${ACCENT_CLASS[column.accent] || ''}`}>{formatPayrollSheetValue(row.values[column.code], column.decimals)}</td>
                                        ))}
                                        <td />
                                    </tr>
                                );
                            })}
                            <tr className="font-bold">
                                <td colSpan={3} className="text-center">TỔNG CỘNG</td>
                                {sheet.columns.map((column) => <td key={column.code} className="text-right whitespace-nowrap">{column.additive ? formatPayrollSheetValue(sheet.totals[column.code], column.decimals) : ''}</td>)}
                                <td />
                            </tr>
                        </tbody>
                    </table>
                    <div className="grid grid-cols-3 text-center text-xs font-bold mt-4">
                        <div>NGƯỜI LẬP BẢNG<div className="h-16" /></div><div>KẾ TOÁN<div className="h-16" /></div><div>GIÁM ĐỐC<div className="h-16" /></div>
                    </div>
                </div>
            )) : (
                <div className="grid grid-cols-2 gap-4">
                    {model.sheets.flatMap((sheet) => sheet.rows.map((row) => (
                        <div key={`${sheet.templateCode}-${row.soHd}`} className="border border-gray-500 p-3 text-xs break-inside-avoid">
                            <h2 className="text-center font-bold text-sm">PHIẾU LƯƠNG THÁNG {String(wb.period.THANG).padStart(2, '0')}/{wb.period.NAM}{draft ? ' (NHÁP)' : ''}</h2>
                            <p className="mt-1"><b>{row.hoTen}</b> — Số HĐ {row.soHd} — Tổ {row.to}</p>
                            <p className="text-gray-600 mb-1">{sheet.templateName}</p>
                            <table>
                                <tbody>
                                    {sheet.columns.filter((column) => row.values[column.code]).map((column) => (
                                        <tr key={column.code} className={ACCENT_CLASS[column.accent] || ''}>
                                            <td>{column.header}</td>
                                            <td className="text-right">{formatPayrollSheetValue(row.values[column.code], column.decimals)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                            <div className="flex justify-between mt-3"><span>Người nhận</span><span>Người lập</span></div>
                            <div className="h-10" />
                        </div>
                    )))}
                </div>
            )}
        </div>
    );
};

export default BangCongLuongIn;
